import { useState, useEffect } from 'react';

export default function PrimeNavbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { id: 'funnels', label: 'Çözümlerimiz', icon: 'ri-stack-line' },
    { id: 'process', label: 'Süreç', icon: 'ri-flow-chart' },
    { id: 'comparison', label: 'Karşılaştırma', icon: 'ri-bar-chart-box-line' },
    { id: 'references', label: 'İş Ortaklarımız', icon: 'ri-award-line' },
  ];

  return (
    <>
      {/* Top Info Bar */}
      <div className="bg-primary-dark border-b border-accent/20 py-2.5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              <a 
                href="tel:+905524400989" 
                className="flex items-center gap-2 text-white/70 hover:text-accent transition-colors duration-300 cursor-pointer"
              >
                <i className="ri-phone-line text-accent"></i>
                <span className="hidden sm:inline">+90 552 440 09 89</span>
              </a>
              <a 
                href="mailto:sales@primesalespro.com" 
                className="flex items-center gap-2 text-white/70 hover:text-accent transition-colors duration-300 cursor-pointer"
              >
                <i className="ri-mail-line text-accent"></i>
                <span className="hidden md:inline">sales@primesalespro.com</span>
              </a>
            </div>
            <div className="flex items-center gap-4 text-white/60 text-xs">
              <div className="flex items-center gap-1.5">
                <i className="ri-time-line text-accent"></i>
                <span className="hidden lg:inline">7/24 AI Destek</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-shield-check-line text-accent"></i>
                <span className="hidden lg:inline">Güvenli Sistem</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav
        className={`sticky top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-primary/95 backdrop-blur-xl shadow-2xl shadow-primary/30 border-b border-accent/10' 
            : 'bg-primary/95 backdrop-blur-xl'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo & Tagline */}
            <div
              className={`flex items-center gap-4 transition-all duration-700 ${
                isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
              }`}
            >
              <div className="relative group cursor-pointer">
                <div className="absolute inset-0 bg-accent/20 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <img 
                  src="https://static.readdy.ai/image/9bc2349a8039d11e997391420dde935f/7e8d516b1bde13296cc94cedaa9bb88e.png" 
                  alt="Prime Sales Pro" 
                  className="h-14 w-auto relative z-10 transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navItems.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`relative flex items-center gap-2 transition-all duration-700 cursor-pointer font-medium text-sm tracking-wide group ${
                    isScrolled
                      ? 'text-white/80 hover:text-accent'
                      : 'text-white/90 hover:text-accent'
                  } ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
                  style={{ transitionDelay: `${(index + 1) * 80}ms` }}
                >
                  <i className={`${item.icon} text-base transition-transform duration-300 group-hover:scale-110`}></i>
                  {item.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-accent to-gold-400 transition-all duration-300 group-hover:w-full"></span>
                </button>
              ))}
            </div>

            {/* Desktop CTA Buttons */}
            <div
              className={`hidden lg:flex items-center gap-3 transition-all duration-700 ${
                isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
              }`}
              style={{ transitionDelay: '400ms' }}
            >
              <a
                href="https://wa.me/905524400989"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap text-sm font-medium group border ${
                  isScrolled
                    ? 'bg-white/5 text-white hover:bg-white/10 border-white/10 hover:border-accent/30'
                    : 'bg-white/10 text-white hover:bg-white/20 border-white/20 hover:border-accent/30'
                }`}
              >
                <i className="ri-whatsapp-line text-lg text-green-400 transition-transform duration-300 group-hover:scale-110"></i>
                WhatsApp
              </a>
              <button
                onClick={() => scrollToSection('analiz-formu')}
                className="relative px-6 py-2.5 bg-gradient-to-r from-accent via-gold-500 to-accent-light hover:from-accent-light hover:via-gold-400 hover:to-accent text-primary rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap text-sm font-bold shadow-lg shadow-accent/30 hover:shadow-accent/50 hover:scale-105 overflow-hidden group"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <i className="ri-rocket-line"></i>
                  Ücretsiz Analiz
                </span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2.5 rounded-lg transition-all duration-300 cursor-pointer border ${
                isScrolled
                  ? 'text-white hover:bg-white/10 border-white/10'
                  : 'text-white hover:bg-white/20 border-white/20'
              }`}
            >
              <i className={`text-2xl transition-transform duration-300 ${isMobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        {/* Backdrop */}
        <div
          onClick={() => setIsMobileMenuOpen(false)}
          className={`absolute inset-0 bg-primary/98 backdrop-blur-xl transition-opacity duration-500 ${
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
        ></div>

        {/* Menu Content */}
        <div
          className={`absolute top-[116px] left-0 right-0 bg-primary-dark/98 backdrop-blur-xl border-t border-b border-accent/20 transition-transform duration-500 ${
            isMobileMenuOpen ? 'translate-y-0' : '-translate-y-full'
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
            {/* Navigation Links */}
            <div className="space-y-3">
              {navItems.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`w-full flex items-center gap-3 px-5 py-4 text-white/90 hover:text-accent hover:bg-white/5 rounded-xl transition-all duration-300 cursor-pointer font-medium border border-transparent hover:border-accent/30 ${
                    isMobileMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                  }`}
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  <i className={`${item.icon} text-xl text-accent`}></i>
                  {item.label}
                </button>
              ))}
            </div>

            {/* Mobile CTA Buttons */}
            <div className="space-y-3 pt-4 border-t border-accent/20">
              <a
                href="https://wa.me/905524400989"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-5 py-4 bg-white/5 text-white hover:bg-white/10 border border-white/10 hover:border-accent/30 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap text-sm font-medium"
              >
                <i className="ri-whatsapp-line text-xl text-green-400"></i>
                WhatsApp ile İletişim
              </a>
              <button
                onClick={() => scrollToSection('analysis-form')}
                className="w-full px-6 py-4 bg-gradient-to-r from-accent via-gold-500 to-accent-light text-primary rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap text-sm font-bold shadow-lg shadow-accent/30 flex items-center justify-center gap-2"
              >
                <i className="ri-rocket-line"></i>
                Ücretsiz Analiz Başlat
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
